import React, { FC } from 'react';
import styled from 'styled-components';
import { Button, Icon } from 'components';
import { DEFAULT_ORG_ADMIN_JOB_TITLE } from 'config/const';
import { theme, toRem } from 'design';
import { EmployeeType, IEmployee, IEmployeeSite, IEmployerSite } from 'types';
import { formatJobTitle } from 'utils/jobs';
import UserInfo from '../../../UserInfo/UserInfo';

const Layout = styled.li`
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: ${toRem(16)};

  &:not(:last-child) {
    border-bottom: 1px solid ${theme.color.neutral[300]};
  }

  @media (min-width: 500px) {
    flex-direction: row;
  }

  &.selected {
    background-color: #eefdff;
  }
`;

const EmployeeInfo = styled(UserInfo)`
  min-width: 100%;
  width: 100%;
  max-width: 100%;

  .user-info-layout {
    max-width: calc(100% - ${toRem(52)}); // full width of user info minus (avatar width + avatar margin)

    & > div {
      max-width: ${toRem(44)};
    }
  }

  section {
    justify-content: center;

    p {
      width: 100%;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;

      &:first-child {
        margin-bottom: 0;
        line-height: ${toRem(18)};
      }

      &:last-child {
        font-size: ${toRem(12)};
        text-transform: unset;
      }
    }
  }

  @media (min-width: 500px) {
    min-width: 50%;
    width: 50%;
    max-width: 50%;
  }
`;

const EmployeeMetaInfo = styled.div`
  display: flex;
  min-width: 100%;
  width: 100%;
  max-width: 100%;
  margin-top: ${toRem(16)};

  @media (min-width: 500px) {
    min-width: 50%;
    width: 50%;
    max-width: 50%;
    margin-top: 0;
  }
`;

const EmployeeInfoContainer = styled.div`
  display: flex;
  flex-direction: column;
  min-width: 50%;
  width: 50%;
  max-width: 50%;
  padding-left: 0;

  &.align-right {
    align-items: flex-end;
  }

  & > span {
    display: block;

    &:first-child {
      margin-bottom: ${toRem(4)};
      font-size: ${toRem(12)};
      color: ${theme.color.neutral[500]};
    }

    &:last-child {
      width: 100%;
      font-size: ${toRem(14)};
      color: ${theme.color.neutral[900]};
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
      direction: ltr;
    }
  }

  @media (min-width: 500px) {
    padding-left: ${toRem(16)};
  }
`;

const SelectedEmployee = styled.div`
  margin-top: ${toRem(12)};
  color: ${theme.color.secondary[400]};
  font-weight: ${theme.font.weight.semiBold};
  font-size: ${toRem(12)};

  svg {
    fill: ${theme.color.secondary[400]};
    margin-right: ${toRem(4)};
  }
`;

interface IEmployeeListItemProps {
  dataCy: string;
  employee: IEmployee;
  site: IEmployerSite;
  isSelected: boolean;
  isLinked: boolean;
  onSelect(employee: IEmployee): void;
}

const EmployeeListItem: FC<IEmployeeListItemProps> = ({ employee, site, isSelected, isLinked, onSelect, dataCy }) => {
  // get employee site
  const employeeSite: IEmployeeSite = employee.sites?.find((s) => s.id === site?.id);

  return (
    <Layout className={!isLinked && isSelected && 'selected'} data-cy={`${dataCy}-employee-list-item`}>
      <EmployeeInfo image={employee.user?.avatarUrl} imageSize="medium" username={employee.name} info={formatJobTitle(employee.type === EmployeeType.ADMIN ? DEFAULT_ORG_ADMIN_JOB_TITLE : employeeSite?.job?.title || '')} />
      <EmployeeMetaInfo>
        <EmployeeInfoContainer>
          <span>Employee ID</span>
          <span>{employeeSite?.employeeId || '--'}</span>
        </EmployeeInfoContainer>
        <EmployeeInfoContainer className="align-right">
          {isLinked && (
            <SelectedEmployee>
              <Icon icon="Link" />
              Connected
            </SelectedEmployee>
          )}
          {!isLinked && !isSelected && <Button onClick={() => onSelect(employee)} label="Select" size="small" />}
          {!isLinked && isSelected && (
            <SelectedEmployee>
              <Icon icon="CheckmarkThick" />
              Selected
            </SelectedEmployee>
          )}
        </EmployeeInfoContainer>
      </EmployeeMetaInfo>
    </Layout>
  );
};

export default EmployeeListItem;
